"use client"

import { useState, useEffect } from "react"
import { useTranslations } from "next-intl"
import {
  Paper,
  Text,
  Stack,
  Group,
  Divider,
  SimpleGrid,
  Title,
} from "@forestsync/ui"
import type { TransactionDetail } from "@/types/glassroom"
import { TRANSACTION_TYPES } from "@forestsync/db"
import { QRCode, PrintCertificateButton } from "@/components/glassroom/common"

interface CertificatePDFProps {
  transaction: TransactionDetail
}

export function CertificatePDF({ transaction }: CertificatePDFProps) {
  const t = useTranslations("certificate")
  const [certUrl, setCertUrl] = useState("")

  useEffect(() => {
    setCertUrl(window.location.href)
  }, [])

  if (transaction.transactionType !== TRANSACTION_TYPES.RETIREMENT) {
    return null
  }

  const tokenSerials = transaction.forests.flatMap((f) =>
    f.tokens.map((tok) => tok.serialNumber),
  )
  const vintage = new Date(transaction.timestamp).getFullYear()
  const primaryForest = transaction.forests[0]

  return (
    <>
      <PrintCertificateButton />

      {/* Certificate card — always visible; page hides other content during print via print:hidden on parent */}
      <Paper
        withBorder
        p="xl"
        radius="lg"
        mt="md"
        className="print:shadow-none print:border-gray-300"
      >
        <Stack gap="lg">
          {/* Header */}
          <div className="text-center">
            <Text size="sm" fw={600} className="uppercase tracking-widest" c="dimmed">
              ForestSync Registry
            </Text>
            <Title order={3} mt={4}>
              {t("certificateTitle")}
            </Title>
          </div>

          <Divider />

          {/* Details */}
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <div>
              <Text size="xs" c="dimmed">{t("retiredBy")}</Text>
              <Text fw={600}>{transaction.fromUserName ?? "N/A"}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">{t("retirementDate")}</Text>
              <Text fw={600}>
                {new Date(transaction.timestamp).toLocaleDateString()}
              </Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">{t("vintage")}</Text>
              <Text fw={600}>{vintage}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">{t("creditType")}</Text>
              <Text fw={600}>{primaryForest?.sinkName ?? "N/A"}</Text>
            </div>
          </SimpleGrid>

          {/* Serial numbers */}
          {tokenSerials.length > 0 && (
            <div>
              <Text size="xs" c="dimmed" mb={6}>
                {t("serialNumbers")} ({tokenSerials.length})
              </Text>
              <div className="flex flex-wrap gap-1">
                {tokenSerials.slice(0, 24).map((serial) => (
                  <Text
                    key={serial}
                    size="xs"
                    ff="monospace"
                    className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded"
                  >
                    {serial}
                  </Text>
                ))}
                {tokenSerials.length > 24 && (
                  <Text size="xs" c="dimmed">
                    +{tokenSerials.length - 24} more
                  </Text>
                )}
              </div>
            </div>
          )}

          <Divider />

          {/* Footer with QR */}
          <Group justify="space-between" align="flex-end">
            <Stack gap={2}>
              <Text size="xs" c="dimmed">{t("certifiedBy")}</Text>
              <Text size="xs" ff="monospace" c="dimmed">
                {transaction.refId}
              </Text>
            </Stack>
            {certUrl && <QRCode value={certUrl} size={90} />}
          </Group>
        </Stack>
      </Paper>
    </>
  )
}
